import {useApolloClient, useQuery} from "@apollo/client";
import * as Sentry from "@sentry/react";
import {PropsWithChildren, createContext, useEffect} from "react";

import {DEFAULT_USER_CONTEXT, getUserHasFeature} from "@/components/context/utils.ts";
import {Redirect} from "@/components/router/Redirect.tsx";
import {useSessionHeartbeat} from "@/lib/hooks/useSessionHeartbeat.ts";
import {loginZendeskMessaging} from "@/lib/integrations/zendesk.ts";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {CurrentUserContext, GQL_USER_CONTEXT} from "@/lib/queries/accounts.ts";

export const UserContext = createContext<CurrentUserContext & {userHasFeature: (name: string) => {}}>(
  DEFAULT_USER_CONTEXT
);

export const UserContextProvider = ({children}: PropsWithChildren) => {
  const {data, loading, error} = useQuery<CurrentUserContext>(GQL_USER_CONTEXT);
  const {reFetchObservableQueries} = useApolloClient();
  const userId = data?.user?.id;

  useEffect(() => {
    reFetchObservableQueries();
  }, [reFetchObservableQueries, data]);

  useSessionHeartbeat({
    heartbeatInterval: ((data?.user?.account?.inactive_session_timeout || 0) / 2) * 1000, // send heartbeat every 1/2 of the timeout period
  });

  useEffect(() => {
    if (userId) {
      loginZendeskMessaging();
    }
  }, [userId]);

  if (loading) {
    return null;
  }

  if (error) {
    return <Redirect url={`${URL_ORIGINAL_WEBSITE}/accounts/login`} />;
  }

  Sentry.setUser({
    accountuser_id: data?.user?.id,
    account_id: data?.user?.account?.id,
  });

  return (
    <UserContext.Provider
      value={{userHasFeature: getUserHasFeature(data?.user?.account?.feature_flags), ...(data || DEFAULT_USER_CONTEXT)}}
    >
      {children}
    </UserContext.Provider>
  );
};
